import {
  AgentThreadControlSignalDtoSignal,
  AgentThreadVoStatus,
  api,
  ContentType,
} from "@/api";
import type { AgentThreadTaskStatus, AgentThreadVo } from "@/api/Api";
import { RawArtifact } from "@/types/artifact";
import { action, makeAutoObservable, observable } from "mobx";

type SetKeys = keyof Pick<
  ThreadStore,
  | "loading"
  | "threads"
  | "thread"
  | "rawArtifacts"
  | "artifactsRequestStatus"
  | "rtcTasks"
>;

export type ArtifactsRequestStatus =
  | "idle"
  | "loading"
  | "success"
  | "not_found"
  | "error";

export interface RtcTask {
  id: string;
  task_type: string;
  thread_id: string;
  data:
    | {
        tool_type: "generate_image_for_shot_by_index";
        init_args: {
          i2i_model_name: string;
          t2i_model_name: string;
          user_prompt: string;
          user_specified_images: string[];
        };
        call_args: {
          shot_index: number;
        };
      }
    | {
        tool_type: "generate_video_for_shot_by_index";
        init_args: {
          t2v_model_name: string;
          i2v_model_name: string;
          is2v_model_name: string;
          user_prompt: string;
          line_content: string;
          user_specified_images: string[];
          avatar_voice_id: string;
          avatar_voice_path: string;
          generate_type: "normal" | "avatar";
        };
        call_args: {
          shot_index: number;
        };
      }
    | {
        tool_type: "generate_reference_image_by_index";
        init_args: {
          i2i_model_name: string;
          t2i_model_name: string;
          user_prompt: string;
          user_specified_images: string[];
        };
        call_args: {
          index: number;
        };
      };
  status: AgentThreadTaskStatus;
  message?: string;
  create_time: string;
  update_time: string;
}

export class ThreadStore {
  @observable threads: AgentThreadVo[] = [];
  @observable loading: boolean = false;
  @observable threadsLoading: boolean = false;
  @observable updateUserLoading: boolean = false;
  @observable thread: AgentThreadVo | null = null;
  @observable threadId: string | null = null;
  @observable rawArtifacts: RawArtifact | null = null;
  @observable artifactsRequestStatus: ArtifactsRequestStatus = "idle";
  @observable rtcTasks: RtcTask[] = [];

  isPosterUpdated: boolean = false;

  constructor() {
    // 使用装饰器时不需要 makeAutoObservable
    makeAutoObservable(this);
  }

  get isThreadSuccess() {
    return (
      this.thread?.status === AgentThreadVoStatus.Success &&
      !!this.rawArtifacts?.["<Final Result Artifact>"]?.content
    );
  }

  @action setThreadId(id: string) {
    if (this.threadId && this.threadId === id) {
      return;
    }
    this.threadId = id;
    this.set("thread", null);
    this.set("rawArtifacts", null);
    this.set("artifactsRequestStatus", "idle");
    this.set("rtcTasks", []);
  }

  @action clearThread() {
    this.threadId = null;
    this.set("thread", null);
    this.set("rawArtifacts", null);
    this.set("artifactsRequestStatus", "idle");
    this.set("rtcTasks", []);
    this.isPosterUpdated = false;
  }

  @action set<K extends SetKeys>(k: K, v: ThreadStore[K]) {
    if (k === "thread") {
      const incomingThread = v as AgentThreadVo | null;
      if (!incomingThread) {
        this.thread = null;
        return;
      }

      const nextThread: AgentThreadVo = { ...incomingThread };

      // 如果 incomingThread 没有 status，则使用 previous status
      if (!nextThread.status && this.thread?.status) {
        nextThread.status = this.thread.status;
      }

      this.thread = nextThread;
      return;
    }

    this[k] = v as never;
  }

  @action async fetchThread(id: string): Promise<AgentThreadVo> {
    const res = await api.threads.getThreadDetail(id);
    const thread = res.data;
    this.set("thread", thread);
    return thread;
  }

  @action async stopRunning() {
    if (!this.threadId) {
      return;
    }
    await api.threads.controlThread(this.threadId, {
      signal: AgentThreadControlSignalDtoSignal.ValueCtrlStopImmediately,
    });
    this.set("thread", {
      ...this.thread,
      status: AgentThreadVoStatus.Stopping,
    });
  }

  @action async updateThreadPoster(posterUrl: string) {
    if (
      !this.thread ||
      !this.thread.owner ||
      this.thread.imageUrl ||
      this.isPosterUpdated
    ) {
      return;
    }
    console.log("updateThreadPoster", posterUrl);
    const res = await api.threads.updateThread(this.thread.id!, {
      imageUrl: posterUrl,
    });
    this.set("thread", res.data);
    this.isPosterUpdated = true;
    return res.data;
  }

  @action async fetchThreads(options?: {
    offset?: number;
    limit?: number;
  }): Promise<AgentThreadVo[]> {
    this.threadsLoading = true;
    try {
      const res = await api.threads.listThreads({
        offset: options?.offset,
        limit: options?.limit,
      });
      this.set("threads", res.data?.list || []);
      return res.data?.list || [];
    } finally {
      this.threadsLoading = false;
    }
  }

  @action async sendMessage(
    threadId: string,
    data: { files: File[]; text: string }
  ) {
    const formData = new FormData();

    for (const file of data.files) {
      formData.append("files", file);
    }

    formData.append("text", data.text);

    await api.request<AgentThreadVo, unknown>({
      path: `/api/v1/threads/${threadId}/messages`,
      method: "POST",
      body: formData,
      type: ContentType.FormData,
    });
  }

  @action async createThread(data: {
    urls: string[];
    user_input: string;
    auto_mode: boolean;
  }) {
    const formData = new FormData();

    formData.append("file_urls", JSON.stringify(data.urls));
    formData.append("user_input", data.user_input);
    formData.append("type", "interactive");
    formData.append("auto_mode", data.auto_mode.toString());

    try {
      const res = await api.request<AgentThreadVo, unknown>({
        path: `/api/v1/threads`,
        method: "POST",
        body: formData,
        type: ContentType.FormData,
      });

      this.fetchThreads();
      return res.data;
    } finally {
      this.updateUserLoading = false;
    }
  }

  @action async updateThreadTitle(threadId: string, title: string) {
    const res = await api.threads.updateThread(threadId, {
      title,
    });
    this.set("thread", res.data);
    return res.data;
  }
}

export const threadStore = new ThreadStore();
